import { motion } from "framer-motion";
import { Sparkles, UserCheck } from "lucide-react";
import ActionLink from "../common/ActionLink";
import { contactPageData } from "../../data/contactData";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.66,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

function ContactHero() {
  const { hero } = contactPageData;

  return (
    <section className="relative overflow-hidden pb-16 pt-28 md:pb-20 md:pt-36">
      <div className="absolute inset-0 bg-gradient-to-br from-teal-800 via-emerald-950 to-midnight-navy" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_8%,rgba(255,255,255,0.1),transparent_32%),radial-gradient(circle_at_86%_20%,rgba(52,211,153,0.15),transparent_28%)]" />
      <div className="absolute -left-16 top-20 h-56 w-56 rounded-full bg-teal-400/10 blur-[100px]" />
      <div className="absolute -right-10 top-12 h-64 w-64 rounded-full bg-emerald-300/10 blur-[100px]" />
      <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0, rgba(255,255,255,0.02) 2px, transparent 2px, transparent 16px)' }} />

      <div className="relative mx-auto w-full max-w-7xl px-6">
        <div className="grid gap-12 lg:grid-cols-[1fr_400px] lg:items-center">
          <div>
            <motion.p
              custom={0.05}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white shadow-sm backdrop-blur-md"
            >
              <Sparkles className="h-3.5 w-3.5 text-teal-300" aria-hidden="true" />
              {hero.kicker}
            </motion.p>

            <motion.h1
              custom={0.12}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="mt-6 text-3xl leading-[1.12] font-bold tracking-tight text-white sm:text-4xl md:text-6xl drop-shadow-sm"
            >
              {hero.title}
            </motion.h1>

            <motion.p
              custom={0.2}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="mt-6 max-w-2xl text-sm leading-relaxed text-teal-50/90 sm:text-base md:text-lg"
            >
              {hero.subtitle}
            </motion.p>

            <motion.div
              custom={0.26}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="mt-6 flex flex-wrap gap-2"
            >
              {hero.trustChips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-white/20 bg-teal-900/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-white backdrop-blur-sm"
                >
                  {chip}
                </span>
              ))}
            </motion.div>

            <motion.div
              custom={0.32}
              variants={reveal}
              initial="hidden"
              animate="visible"
              className="mt-10 flex flex-col gap-4 sm:flex-row"
            >
              <div className="inline-flex">
                <ActionLink variant="ctaPrimary" to={hero.primary.to} label={hero.primary.label} />
              </div>
              <div className="inline-flex text-white hover:text-white">
                <ActionLink variant="ctaSecondary" to={hero.secondary.to} label={hero.secondary.label} showArrow={false} />
              </div>
            </motion.div>
          </div>

          <motion.aside
            custom={0.3}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="rounded-[2rem] border border-white/20 bg-teal-900/30 p-8 shadow-luxury backdrop-blur-xl"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-teal-200/90">
              Your First Step, Made Clear
            </p>
            <h2 className="mt-3 text-xl font-bold leading-tight text-white sm:text-2xl md:text-3xl">
              Speak with a Team That Understands the Journey
            </h2>

            <div className="mt-8 space-y-4">
              {hero.supportPoints.map((point) => (
                <div
                  key={point}
                  className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/5 px-5 py-4"
                >
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[0.8rem] bg-teal-500/20 text-teal-300">
                    <UserCheck className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <p className="text-sm font-medium text-white leading-relaxed pt-1">{point}</p>
                </div>
              ))}
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

export default ContactHero;
